// Shared report semantics. A COC's office is the Performing Office chosen in the app, or, when none was
// chosen, the sender's own sign-up Company Details (see withDefaultOffice). Never infer it from an email.
import { COUNTRY_NAME_MAPPING, US_STATE_MAPPING } from './map-dashboard/geo-constants';

export const COC_TYPES = ['Asbestos Air', 'Bulk', 'Air', 'Forensic/Police', 'Haz/Multi-Bulk', 'Multi-Air'];

export function toMillis(value) {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value === 'object' && (value.seconds != null || value._seconds != null)) return Number(value.seconds ?? value._seconds) * 1000;
    if (typeof value === 'number' || /^\d+(\.\d+)?$/.test(value)) {
        const n = Number(value);
        return Number.isFinite(n) ? (n < 1e11 ? n * 1000 : n) : 0;
    }
    // A bare date is a calendar day, not UTC midnight: Date.parse would move it to the previous day in US timezones.
    const day = typeof value === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (day) return calendarDay(day[1], day[2], day[3]);
    return Date.parse(value) || 0;
}
const calendarDay = (year, month, day) => {
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return date.getMonth() === Number(month) - 1 ? date.getTime() : 0;
};

// documentCreatedAt is Firestore's own creation time for the Users document; the mobile app stores no signup date.
export const joinedAt = user => toMillis(user.joinedAt || user.createdAt || user.createdDate || user.registrationDate || user.signupDate || user.documentCreatedAt);
const sendTimestamp = coc => toMillis(coc.submittedAt || coc.sentAt || coc.sendDateTimeStamp || coc.sentDateTimeStamp);
// The mobile app records no send time. Its Submission ID (YYYYMMDD-COC-XXXXXXXX) is generated on the
// first send from the sender's local date, so it is the only record of the day a COC was sent.
export const submissionDate = coc => {
    const id = /^(\d{4})(\d{2})(\d{2})-/.exec(coc.submissionId || coc.submissionNumber || coc.submissionID || '');
    return id ? calendarDay(id[1], id[2], id[3]) : 0;
};
export const hasSubmissionTime = coc => !!sendTimestamp(coc);
export const submittedAt = coc => sendTimestamp(coc) || submissionDate(coc);
export const isSubmitted = coc => !!submittedAt(coc) || coc.send === true || coc.send === 1 || coc.status === 'submitted' || coc.status === 'sent';
export const cocTimestamp = coc => submittedAt(coc) || toMillis(coc.createdAt) || toMillis(coc.collectionDateTimeStamp || coc.collectionDate);
export const submissionNumber = coc => coc.submissionId || coc.submissionNumber || coc.submissionID || '—';
const officeSnapshot = coc => {
    const raw = coc.performingOfficeDetails || coc.performingOffice;
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw !== 'string') return null;
    try { const parsed = JSON.parse(raw); return parsed && typeof parsed === 'object' ? parsed : null; } catch (e) { return null; }
};

export const performingOfficeId = coc => coc.performingOfficeId || officeSnapshot(coc)?.id || coc.companyDetailsId || coc.companyId || coc.defaultOfficeId || null;
// The sender's sign-up Company Details by user id (the first record when a user has several).
export const companiesByUser = companies => companies.reduce((map, company) => company.userId && !map[company.userId] ? { ...map, [company.userId]: company } : map, {});
// With no Performing Office chosen, the mobile app puts the sender's sign-up Company Details on the COC
// (SelectLabChoice, Shipping), so that company is the office. `company` is the sender's CompanyDetails record.
export const withDefaultOffice = (coc, company) => company?.id && !performingOfficeId(coc) ? { ...coc, defaultOfficeId: company.id } : coc;
export function performingOffice(coc, companies = {}) {
    const id = performingOfficeId(coc);
    const snapshot = officeSnapshot(coc) || {};
    // Real COC snapshots nest the office record under companyDetails/billingDetails.
    const office = { ...(snapshot.billingDetails || {}), ...snapshot, ...(snapshot.companyDetails || {}), ...(companies[id] || {}) };
    if (id) office.id = id;
    return office;
}
export function companyNameForCoc(coc, companies = {}) {
    const office = performingOffice(coc, companies);
    return office.companyName || office.name || coc.performingOfficeName || coc.companyName || 'Unassigned performing office';
}
const analysisKey = value => String(value || '').trim().toLowerCase();
// COCs store the analyses chosen in the app, never a COC type. The Tests catalog says which
// COC type each analysis belongs to, keyed here by category + analysis and by analysis alone.
export function analysisTypeLookup(tests = []) {
    const lookup = {};
    tests.forEach(test => {
        const type = test.htmlPdfType || test.coc_type_name;
        const analysis = analysisKey(test.analysis_type);
        if (!type || !analysis) return;
        lookup[`${analysisKey(test.analysis_category_name)}|${analysis}`] = type;
        if (!lookup[analysis]) lookup[analysis] = type;
    });
    return lookup;
}
export function cocType(coc, types = {}, analysisTypes = {}) {
    const analyses = [].concat(coc.typesOfAnalysis || []).filter(a => typeof a === 'string');
    const fromCatalog = analyses.map(a => analysisTypes[`${analysisKey(coc.analysisCategory)}|${analysisKey(a)}`] || analysisTypes[analysisKey(a)]).find(Boolean);
    const raw = coc.cocTypeName || coc.coc_type_name || types[coc.cocTypeId || coc.coc_type_id]?.name || coc.cocType || coc.type || fromCatalog || (typeof coc.typesOfAnalysis === 'string' ? coc.typesOfAnalysis : coc.analysisCategory);
    if (typeof raw !== 'string') return null;
    const key = raw.toLowerCase().replace(/[^a-z]/g, '');
    // Includes the mobile app's own type names (Multi Bulk, Haz, Ag/Can, Forensics, Multi Air).
    const aliases = { asbestosair: 'Asbestos Air', bulk: 'Bulk', air: 'Air', forensicpolice: 'Forensic/Police', forensic: 'Forensic/Police', forensics: 'Forensic/Police', police: 'Forensic/Police', hazmultibulk: 'Haz/Multi-Bulk', multibulk: 'Haz/Multi-Bulk', haz: 'Haz/Multi-Bulk', agcan: 'Haz/Multi-Bulk', multiair: 'Multi-Air' };
    return aliases[key] || null;
}
export const formatTimestamp = value => toMillis(value) ? new Date(toMillis(value)).toLocaleString() : '—';
// Shows the send time only when one was recorded; otherwise just the send date.
export const formatSubmitted = coc => !submittedAt(coc) ? '—' : hasSubmissionTime(coc) ? formatTimestamp(submittedAt(coc)) : new Date(submittedAt(coc)).toLocaleDateString();
// A lab's counts can be restarted from a moment in time (Laboratory.cocCountResetAt); earlier COCs are kept
// but no longer counted for that lab. Sent COCs carry only a send day, so the creation time is honoured too.
export const countedForLab = (coc, lab) => {
    const since = toMillis(lab?.cocCountResetAt);
    return !since || toMillis(coc.createdAt) >= since || cocTimestamp(coc) >= since;
};
// Labs such as EMSL have one record per city, so the name alone is ambiguous.
export const labDisplayName = lab => {
    const name = String(lab?.labName || '').trim(), city = String(lab?.labCity || '').trim();
    return city && !name.toLowerCase().includes(city.toLowerCase()) ? [name, city].filter(Boolean).join(', ') : name;
};

const CA_PROVINCES = { AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick', NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', NT: 'Northwest Territories', NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan', YT: 'Yukon' };
// Common non-US state/province spellings seen in office records.
const EXTRA_STATE_ALIASES = { GUJ: 'Gujarat', GJ: 'Gujarat', Guajrat: 'Gujarat', UP: 'Uttar Pradesh', RAJ: 'Rajasthan' };
const COUNTRY_DISPLAY = { ...COUNTRY_NAME_MAPPING, 'United States': 'United States', 'United States of America': 'United States', 'USA': 'United States', 'US': 'United States', 'U.S.': 'United States', 'U.S.A.': 'United States', 'inida': 'India' };
const lowerIndex = mapping => Object.fromEntries(Object.entries(mapping).map(([key, value]) => [key.toLowerCase(), value]));
const STATE_LOOKUP = lowerIndex({ ...US_STATE_MAPPING, ...CA_PROVINCES, ...EXTRA_STATE_ALIASES });
const COUNTRY_LOOKUP = lowerIndex(COUNTRY_DISPLAY);
const geoPart = (value, lookup) => {
    const v = String(value || '').trim();
    if (!v) return '';
    const mapped = lookup[v.toLowerCase()];
    return mapped || v[0].toUpperCase() + v.slice(1);
};
const US_STATE_NAMES = new Set(Object.values(US_STATE_MAPPING));
const CA_PROVINCE_NAMES = new Set(Object.values(CA_PROVINCES));
const IN_STATE_NAMES = new Set(Object.values(EXTRA_STATE_ALIASES));
// Normalized "State, Country" label for an office (e.g. 'Ca'/'USA' -> 'California, United States').
// A US state or Canadian province implies the country when the record leaves it blank.
export const officeRegion = office => {
    const state = geoPart(office.state, STATE_LOOKUP);
    const country = officeCountryName(office, state);
    return [state, country].filter(Boolean).join(', ');
};
// Normalized country label for the analytics "top regions" card; falls back to a lone
// state/province label when no country is recorded or implied.
export const officeCountry = office => {
    const state = geoPart(office.state, STATE_LOOKUP);
    // An unrecognized typed country (e.g. keyboard mash) is left out rather than listed as a region.
    return officeCountryName(office, state) || (String(office.country || '').trim() ? '' : state);
};
// Older office records hold a free-typed country, so only real country names are reported.
let knownCountries;
const knownCountry = value => {
    if (typeof Intl === 'undefined' || !Intl.DisplayNames) return geoPart(value, {});
    if (!knownCountries) {
        const names = new Intl.DisplayNames(['en'], { type: 'region' });
        const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
        knownCountries = {};
        letters.forEach(a => letters.forEach(b => { const name = names.of(a + b); if (name && name !== a + b && name !== 'Unknown Region') knownCountries[name.toLowerCase()] = name; }));
    }
    return knownCountries[String(value || '').trim().toLowerCase()] || '';
};
const officeCountryName = (office, normalizedState) => {
    const country = COUNTRY_LOOKUP[String(office.country || '').trim().toLowerCase()] || knownCountry(office.country);
    if (country) return country;
    if (US_STATE_NAMES.has(normalizedState)) return 'United States';
    if (CA_PROVINCE_NAMES.has(normalizedState)) return 'Canada';
    if (IN_STATE_NAMES.has(normalizedState)) return 'India';
    return '';
};
